import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { TbDownload, TbMenu2, TbX } from "react-icons/tb";
import style from "../styles/navbar.module.css";
import ThemeToggle from "./themeToggle";
import useIsMobile from "../hooks/useIsMobile";
import pdf from "../pages/Images/Akil-Sadik-AI-Automation-Engineer-Resume.pdf";

// Matches the breakpoint where navbar.module.css swaps the sheet for the
// inline desktop links. Keep the two in sync.
const MOBILE_NAV = "(max-width: 1039px)";

const links = [
  { label: "Home", id: "home" },
  { label: "About", id: "about" },
  { label: "Skills", id: "skills" },
  { label: "Experience", id: "experience" },
  { label: "Projects", id: "projects" },
  { label: "Credentials", id: "credentials" },
  { label: "Github", id: "github" },
  { label: "Contact", id: "contact" },
];

export default function Navbar({ theme, toggleTheme }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const isMobileNav = useIsMobile(MOBILE_NAV);

  // Solid backdrop once the page has moved.
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Highlight whichever section currently owns the viewport.
  useEffect(() => {
    const sections = links
      .map((l) => document.getElementById(l.id))
      .filter(Boolean);
    if (!sections.length || !("IntersectionObserver" in window)) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const visible = entries
          .filter((e) => e.isIntersecting)
          .sort((a, b) => b.intersectionRatio - a.intersectionRatio)[0];
        if (visible) setActive(visible.target.id);
      },
      { rootMargin: "-45% 0px -45% 0px", threshold: [0, 0.25, 0.5, 1] }
    );
    sections.forEach((s) => observer.observe(s));
    return () => observer.disconnect();
  }, []);

  // Growing past the mobile breakpoint hides the sheet via CSS. Close it in
  // state too, or the scroll lock below stays on with no menu left to dismiss.
  useEffect(() => {
    if (!isMobileNav && open) setOpen(false);
  }, [isMobileNav, open]);

  // Lock body scroll while the mobile sheet is open.
  useEffect(() => {
    const locked = open && isMobileNav;
    document.body.style.overflow = locked ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open, isMobileNav]);

  return (
    <nav className={`${style.nav} ${scrolled ? style.scrolled : ""}`} id="nav-menu">
      <div className={style.inner}>
        <a className={style.brand} href="#home" onClick={() => setOpen(false)}>
          <span className={style.brandMark}>A</span>kil
          <span className={style.brandDot}>.</span>
        </a>

        <div className={style.desktop}>
          {links.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`${link.id} ${style.link} ${
                active === link.id ? style.linkActive : ""
              }`}
              aria-current={active === link.id ? "page" : undefined}
            >
              {link.label}
              {active === link.id && (
                <motion.span layoutId="navUnderline" className={style.underline} />
              )}
            </a>
          ))}
        </div>

        <div className={style.actions}>
          <ThemeToggle theme={theme} toggle={toggleTheme} />
          <a
            className={`resume ${style.resumeLink}`}
            id="resume-link-1"
            href={pdf}
            download="Akil Sadik - AI Automation Engineer"
          >
            <button id="resume-button-1" className={style.resumeBtn}>
              <TbDownload /> <span>Resume</span>
            </button>
          </a>
          <button
            className={style.hamburger}
            onClick={() => setOpen((o) => !o)}
            aria-label={open ? "Close menu" : "Open menu"}
            aria-expanded={open}
          >
            {open ? <TbX /> : <TbMenu2 />}
          </button>
        </div>
      </div>

      {/* mobile sheet */}
      <motion.div
        className={style.sheet}
        initial={false}
        animate={open ? { height: "auto", opacity: 1 } : { height: 0, opacity: 0 }}
        transition={{ duration: 0.3, ease: "easeInOut" }}
        aria-hidden={!open}
      >
        <div className={style.sheetInner}>
          {links.map((link) => (
            <a
              key={link.id}
              href={`#${link.id}`}
              className={`${style.sheetLink} ${
                active === link.id ? style.sheetLinkActive : ""
              }`}
              onClick={() => setOpen(false)}
            >
              {link.label}
            </a>
          ))}
        </div>
      </motion.div>
    </nav>
  );
}
